import { useState, useRef, useEffect, useCallback } from 'react';
import { useAppStore } from '../store';
import { RefreshCw, ExternalLink, Maximize2, Minimize2, Play, Square } from 'lucide-react';

export function Preview() {
  const { files, project } = useAppStore();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sandboxUrl, setSandboxUrl] = useState<string | null>(null);
  const [sandboxStatus, setSandboxStatus] = useState<'idle' | 'starting' | 'ready' | 'error'>('idle');
  const [sandboxError, setSandboxError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Timer for startup progress
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (sandboxStatus === 'starting' && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [sandboxStatus, startTime]);

  // Start Sandbox preview
  const startSandboxPreview = useCallback(async () => {
    if (!project?.id) return;

    setSandboxStatus('starting');
    setSandboxError(null);
    setStartTime(Date.now());
    setElapsedTime(0);

    try {
      const fileList = Array.from(files.entries()).map(([path, file]) => ({
        path,
        content: file.content,
      }));

      const response = await fetch(`/api/preview/${project.id}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files: fileList }),
      });

      const data = await response.json() as { status?: string; previewUrl?: string; error?: string };

      if (data.status === 'ready' && data.previewUrl) {
        setSandboxUrl(data.previewUrl);
        setSandboxStatus('ready');
      } else {
        setSandboxError(data.error || 'Failed to start preview');
        setSandboxStatus('error');
      }
    } catch (error) {
      setSandboxError(error instanceof Error ? error.message : 'Failed to start sandbox');
      setSandboxStatus('error');
    }
  }, [project?.id, files]);

  // Stop Sandbox preview
  const stopSandboxPreview = useCallback(async () => {
    if (!project?.id) return;

    try {
      await fetch(`/api/preview/${project.id}/stop`, { method: 'POST' });
      setSandboxUrl(null);
      setSandboxStatus('idle');
    } catch (error) {
      console.error('Failed to stop sandbox:', error);
    }
  }, [project?.id]);

  // Hot reload file to sandbox
  const hotReload = useCallback(async (path: string, content: string) => {
    if (!project?.id || sandboxStatus !== 'ready') return;

    try {
      await fetch(`/api/preview/${project.id}/reload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, content }),
      });
    } catch (error) {
      console.error('Hot reload failed:', error);
    }
  }, [project?.id, sandboxStatus]);

  // Auto-start sandbox when project loads with files
  useEffect(() => {
    const appFile = files.get('src/App.tsx') || files.get('src/App.jsx');
    if (appFile && sandboxStatus === 'idle' && project?.id) {
      // Auto-start after a small delay
      const timer = setTimeout(() => {
        startSandboxPreview();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [files, sandboxStatus, project?.id, startSandboxPreview]);

  // Hot reload when files change (if sandbox is ready)
  useEffect(() => {
    if (sandboxStatus === 'ready') {
      const appFile = files.get('src/App.tsx') || files.get('src/App.jsx');
      if (appFile) {
        hotReload('src/App.tsx', appFile.content);
      }
    }
  }, [files, sandboxStatus, hotReload]);

  const handleRefresh = () => {
    if (sandboxUrl && iframeRef.current) {
      iframeRef.current.src = sandboxUrl;
    }
  };

  const handleOpenExternal = () => {
    if (sandboxUrl) {
      window.open(sandboxUrl, '_blank');
    }
  };

  return (
    <div className={`flex flex-col bg-background h-full ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Preview toolbar */}
      <div className="h-10 flex items-center justify-between px-3 border-b border-border bg-background-secondary">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted font-medium uppercase tracking-wider">Preview</span>
          {sandboxStatus === 'ready' && (
            <span className="flex items-center gap-1 text-xs text-green-500">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Live
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Sandbox controls */}
          {sandboxStatus === 'idle' && (
            <button
              onClick={startSandboxPreview}
              className="flex items-center gap-1 px-2 py-1 rounded bg-accent-primary hover:bg-accent-primary/80 text-white text-xs transition-colors"
              title="Start Preview"
            >
              <Play className="w-3 h-3" />
              Start Preview
            </button>
          )}

          {sandboxStatus === 'ready' && (
            <button
              onClick={stopSandboxPreview}
              className="flex items-center gap-1 px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs transition-colors"
              title="Stop Preview"
            >
              <Square className="w-3 h-3" />
              Stop
            </button>
          )}

          {sandboxStatus === 'ready' && (
            <>
              <button
                onClick={handleRefresh}
                className="p-1.5 rounded hover:bg-surface transition-colors"
                title="Refresh preview"
              >
                <RefreshCw className="w-4 h-4 text-text-muted" />
              </button>
              <button
                onClick={handleOpenExternal}
                className="p-1.5 rounded hover:bg-surface transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4 text-text-muted" />
              </button>
            </>
          )}

          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 rounded hover:bg-surface transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4 text-text-muted" />
            ) : (
              <Maximize2 className="w-4 h-4 text-text-muted" />
            )}
          </button>
        </div>
      </div>

      {/* Sandbox error message */}
      {sandboxError && (
        <div className="px-3 py-2 bg-red-500/10 text-red-400 text-xs border-b border-red-500/20 flex items-center justify-between">
          <span>{sandboxError}</span>
          <button
            onClick={startSandboxPreview}
            className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-xs"
          >
            Retry
          </button>
        </div>
      )}

      {/* Preview content */}
      <div className="flex-1 bg-slate-950">
        {sandboxStatus === 'idle' && (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-accent-primary/20 to-purple-500/20 flex items-center justify-center">
                <Play className="w-8 h-8 text-accent-primary" />
              </div>
              <h3 className="text-lg font-medium text-slate-200 mb-2">Preview Your App</h3>
              <p className="text-sm text-slate-500 mb-4">
                Generate some code, then click Start Preview to see it live.
              </p>
            </div>
          </div>
        )}

        {sandboxStatus === 'starting' && (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-primary to-purple-500 flex items-center justify-center mb-4 animate-pulse">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
            <p className="text-sm mb-2">Starting sandbox environment...</p>
            <p className="text-xs text-slate-500">{elapsedTime}s elapsed</p>
            <div className="mt-4 max-w-xs w-full">
              <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-accent-primary to-purple-500 transition-all duration-1000"
                  style={{ width: `${Math.min((elapsedTime / 60) * 100, 95)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-slate-600 mt-1">
                <span>Installing dependencies</span>
                <span>~60s</span>
              </div>
            </div>
          </div>
        )}

        {sandboxStatus === 'error' && (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 p-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-500/20 flex items-center justify-center">
              <span className="text-2xl">⚠️</span>
            </div>
            <h3 className="text-lg font-medium text-red-400 mb-2">Preview Failed</h3>
            <p className="text-sm text-slate-500 text-center max-w-md mb-4">
              {sandboxError || 'An error occurred while starting the preview.'}
            </p>
            <button
              onClick={startSandboxPreview}
              className="px-4 py-2 bg-accent-primary hover:bg-accent-primary/80 text-white text-sm rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {sandboxStatus === 'ready' && sandboxUrl && (
          <iframe
            ref={iframeRef}
            src={sandboxUrl}
            className="w-full h-full border-0"
            title="Preview"
          />
        )}
      </div>
    </div>
  );
}
